import React from 'react';
import { Home, Bath, Square, MapPin, Phone, Mail, Heart, Maximize } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

// URLs de Cloudflare
const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';

// Componente para imágenes de propiedades
const PropertyImage = ({ url, alt, className }) => {
  if (!url) return (
    <div className={`bg-gray-100 flex items-center justify-center ${className}`}>
      <Home className="w-12 h-12 text-gray-300" />
    </div>
  );

  const fullUrl = url.startsWith('http') ? url : `${CLOUDFLARE_PROPERTIES_URL}/${url}`;

  return (
    <img
      src={fullUrl}
      alt={alt}
      className={className}
      onError={(e) => {
        console.error('Error loading property image:', fullUrl);
        e.target.src = '/api/placeholder/800/600';
      }}
    />
  );
};

const ModernHomeFlyer = ({ property }) => {
  // Datos de la propiedad con fallbacks
  const propertyData = property || {
    titulo: 'CASA EXCLUSIVA',
    tipo: 'venta',
    operacion: 'venta',
    precio_soles: 1500000,
    precio_dolares: 400000,
    ubicacion: 'Piura, Piura, Perú',
    habitaciones: 1,
    banos: 1,
    area_total: 500,
    telefono: 'Teléfono',
    email: 'Email',
    descripcion_corta: 'Diseño de alta calidad y acabados de lujo en una ubicación privilegiada.',
    imagenes: [],
    vendedor: {
      nombre: 'INMOBILIARIA',
      foto_perfil: null,
      logo: null
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-PE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Generate property URL - Corregido para usar operacion en lugar de tipo
  const getPropertyUrl = () => {
    const baseUrl = 'https://inkayros.com';
    const path = propertyData.operacion === 'alquiler' ? 'alquiler' : 'propiedad';
    return `${baseUrl}/${path}/${propertyData.id}`;
  };

  return (
    <div className="max-w-4xl mx-auto overflow-hidden rounded-xl shadow-xl">
      {/* Main Container */}
      <div className="bg-white">
        {/* Main House Image with overlay content */}
        <div className="relative h-72">
          <PropertyImage 
            url={propertyData.imagenes[0]}
            alt="Casa en venta" 
            className="w-full h-full object-cover"
          />
          
          {/* Price Banner */}
          <div className="absolute top-6 right-0 bg-blue-600 text-white py-3 px-6 shadow-lg">
            <p className="text-sm font-medium mb-1">PRECIO</p>
            <p className="text-2xl font-bold">S/ {formatPrice(propertyData.precio_soles)}</p>
            <p className="text-sm">USD {formatPrice(propertyData.precio_dolares)}</p>
          </div>
          
          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-6">
            <h1 className="text-4xl font-bold text-white">
              {propertyData.titulo}
            </h1>
            <div className="flex items-center text-white mt-2">
              <MapPin className="w-5 h-5 mr-2" />
              <span>{propertyData.ubicacion}</span>
            </div>
          </div>
        </div>
        
        {/* Carrusel de imágenes */}
        <div className="flex overflow-x-auto gap-2 p-4 bg-white">
          {[1, 2, 3, 4].map((index) => (
            <div key={index} className="flex-none w-24 h-24 rounded-md overflow-hidden">
              <PropertyImage 
                url={propertyData.imagenes[index]}
                alt={`Vista ${index}`} 
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
        
        {/* Características principales */}
        <div className="grid grid-cols-4 gap-0 bg-white border-t border-gray-100">
          <div className="p-4 flex flex-col items-center justify-center border-r border-gray-100">
            <Square className="w-6 h-6 text-blue-600 mb-2" />
            <span className="text-lg font-bold text-gray-800">{propertyData.area_total}m²</span>
            <span className="text-gray-500 text-xs">Área Total</span>
          </div>
          <div className="p-4 flex flex-col items-center justify-center border-r border-gray-100">
            <Home className="w-6 h-6 text-blue-600 mb-2" />
            <span className="text-lg font-bold text-gray-800">{propertyData.habitaciones}</span>
            <span className="text-gray-500 text-xs">Habitaciones</span>
          </div>
          <div className="p-4 flex flex-col items-center justify-center border-r border-gray-100">
            <Bath className="w-6 h-6 text-blue-600 mb-2" />
            <span className="text-lg font-bold text-gray-800">{propertyData.banos}</span>
            <span className="text-gray-500 text-xs">Baños</span>
          </div>
          <div className="p-4 flex flex-col items-center justify-center">
            <Heart className="w-6 h-6 text-blue-600 mb-2" />
            <span className="text-lg font-bold text-gray-800">Cochera</span>
            <span className="text-gray-500 text-xs">Incluida</span>
          </div>
        </div>
        
        {/* Contenido principal con dos columnas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-gray-50">
          {/* Detalles de la propiedad */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Descripción
            </h2>
            <hr className="border-t border-gray-200 mb-4" />
            <ul className="space-y-3">
            {property.descripcion_corta}
            </ul>
          </div>
          
          {/* Información de contacto */}
          <div className="bg-white p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              ¡Contáctanos!
            </h2>
            
            <div className="space-y-4 mb-6">
              <div className="flex items-center">
                <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center mr-3">
                  <Phone className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-gray-800">
                {propertyData.vendedor?.telefono}
                </div>
              </div>
              <div className="flex items-center">
                <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center mr-3">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-gray-800">
                {propertyData.vendedor?.email}
                </div>
              </div>
            </div>
            
            {/* QR Code con instrucciones */}
            <div className="flex items-center gap-4">
              <QRCodeSVG 
                value={getPropertyUrl()} 
                size={100} 
                bgColor="#ffffff"
                fgColor="#0"
                level="L"
              />
              <div>
                <p className="font-semibold text-gray-800">Escanea para</p>
                <p className="text-gray-600">más información</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="bg-blue-600 text-white p-4">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center mb-2 md:mb-0">
            <MapPin className="w-5 h-5 mr-2" />
            <span>{propertyData.ubicacion}</span>
          </div>
          
          <div className="text-center">
            {getPropertyUrl()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernHomeFlyer;